import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from 'src/app/shared/material.module';
import { StoreService } from 'src/app/services/store.service';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { ToWords } from 'to-words';

pdfMake.vfs = pdfFonts.vfs;
const toWords = new ToWords();

@Component({
  selector: 'app-material-issue-print',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, MatProgressBar],
  templateUrl: './material-issue-print.component.html',
  styleUrl: './material-issue-print.component.scss'
})
export class MaterialIssuePrintComponent implements OnInit, OnDestroy {
  issueId!: number;
  issueData: any;
  previewData: any = null;
  isDialogMode = false;
  includeSignature = false;
  pdfDoc: any;
  private originalHtmlOverflow = '';
  private originalBodyOverflow = '';

  constructor(
    private route: ActivatedRoute,
    private storeService: StoreService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { id?: number } | null,
    @Optional() private dialogRef: MatDialogRef<MaterialIssuePrintComponent> | null
  ) {}

  ngOnInit(): void {
    document.body.classList.add('invoice-preview-mode');
    this.originalHtmlOverflow = document.documentElement.style.overflow;
    this.originalBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    this.isDialogMode = !!this.dialogData?.id;
    this.issueId = Number(this.dialogData?.id ?? this.route.snapshot.paramMap.get('id'));
    if (this.issueId) {
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('invoice-preview-mode');
    document.documentElement.style.overflow = this.originalHtmlOverflow;
    document.body.style.overflow = this.originalBodyOverflow;
  }

  printPdf() {
    this.ensurePdfDoc();
    this.pdfDoc?.print();
  }

  downloadPdf() {
    this.ensurePdfDoc();
    this.pdfDoc?.download(`Material-Issue-${this.previewData?.issueCode || this.issueId}.pdf`);
  }

  async sendMail() {
    this.snackBar.open('Material issue mail template configured nahi mila. Mail compose open nahi kiya gaya.', 'Close', {
      duration: 4000
    });
  }

  closePreview() {
    this.dialogRef?.close();
  }

  private loadData(): void {
    this.storeService.getMaterialIssuePrintById(this.issueId).subscribe({
      next: (data: any) => {
        this.issueData = Array.isArray(data) ? data[0] : data;
        this.previewData = this.buildPreviewData(this.issueData || {});
        this.ensurePdfDoc();
      },
      error: (err) => console.error('Error loading material issue print', err)
    });
  }

  private buildPreviewData(source: any) {
    const company = this.pickEntity(source, ['company', 'companymaster', 'companydetails']);
    const warehouse = this.pickEntity(source, ['warehouse', 'whmaster', 'branchwarehouse']);
    const requestCards = this.extractRequestCards(source);
    const items = this.extractItems(source).map((item: any, index: number) => ({
      sr: index + 1,
      productName: this.safeValue(item.productname || item.itemname || item.description),
      uom: this.safeValue(item.uomname || item.uom),
      availableQty: this.formatNumber(item.availablequantity || 0),
      requestCode: this.safeValue(item.materialrequestcode || item.requestcode, ''),
      requestDate: this.formatDateValue(item.materialrequestdate || item.requestdate),
      requestedQty: this.formatNumber(item.standardissuequantity || item.requestquantity || 0),
      pendingQty: this.formatNumber(item.pendingtoissue || item.pendingissueqty || 0),
      issuedQty: this.formatNumber(item.issuedquantity || 0),
      issueRate: this.formatCurrency(item.issuerate || 0, false),
      lineAmount: this.formatCurrency((Number(item.issuedquantity || 0) * Number(item.issuerate || 0)), true),
      remarks: this.safeValue(item.remarks, '')
    }));

    return {
      logo: this.resolveImage([
        company.logoBase64,
        company.logobase64,
        company.logo,
        source.logoBase64,
        source.logobase64
      ]),
      signatureImage: this.resolveImage([
        company.signature,
        company.signatureBase64,
        company.signaturebase64,
        source.signature,
        source.signatureBase64
      ]),
      stampImage: this.resolveImage([
        company.stamp,
        company.stampBase64,
        company.stampbase64,
        source.stamp,
        source.stampBase64
      ]),
      companyInitials: this.getInitials(company.name),
      companyName: this.safeValue(company.name, 'VCS ERP'),
      companyAddress: this.joinAddress([company.address1, company.address2, company.city, company.state, company.country, company.pincode]),
      companyMeta: this.buildCompanyMeta(company),
      signatoryName: this.safeValue(
        company.authorized_signatory_name ||
        company.authorizedSignatoryName ||
        source.authorized_signatory_name ||
        source.authorizedSignatoryName,
        ''
      ),
      signatoryTitle: this.safeValue(
        company.authorized_signatory_title ||
        company.authorizedSignatoryTitle ||
        source.authorized_signatory_title ||
        source.authorizedSignatoryTitle,
        'Authorized Signatory'
      ),
      issueCode: this.safeValue(source.code),
      issueDate: this.formatDateValue(source.issuedt || source.date),
      issueSlipNo: this.safeValue(source.issueslipno, ''),
      issueSlipDate: this.formatDateValue(source.issueslipdt),
      issueType: Number(source.issuetypeid || 1) === 2 ? 'Based On Request' : 'Direct Issue',
      warehouseName: this.safeValue(warehouse.name || warehouse.whname || source.whname || source.warehousename),
      warehouseAddress: this.firstMeaningfulText([
        warehouse.full_address,
        warehouse.fulladdress,
        this.joinAddress([warehouse.address1, warehouse.address2, warehouse.city, warehouse.state, warehouse.country, warehouse.pincode])
      ]),
      issueAmount: this.formatCurrency(source.issueamt || 0, true),
      issueAmountWords: `${toWords.convert(Number(source.issueamt || 0), { currency: true })}/-`,
      remarks: this.safeValue(source.remarks, ''),
      items,
      linkedRequests: requestCards,
      totalItems: items.length
    };
  }

  private ensurePdfDoc() {
    if (!this.previewData) {
      return;
    }

    const body = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'ITEM', style: 'tableHeader' },
        { text: 'REQ CODE', style: 'tableHeader' },
        { text: 'REQ DATE', style: 'tableHeader' },
        { text: 'UOM', style: 'tableHeader' },
        { text: 'AVAILABLE', style: 'tableHeader' },
        { text: 'REQ QTY', style: 'tableHeader' },
        { text: 'PENDING', style: 'tableHeader' },
        { text: 'ISSUED', style: 'tableHeader' },
        { text: 'RATE', style: 'tableHeader' },
        { text: 'AMOUNT', style: 'tableHeader' }
      ],
      ...this.previewData.items.map((item: any) => ([
        { text: String(item.sr), alignment: 'center' },
        { text: item.productName },
        { text: item.requestCode || '-', alignment: 'center' },
        { text: item.requestDate || '-', alignment: 'center' },
        { text: item.uom, alignment: 'center' },
        { text: item.availableQty, alignment: 'right' },
        { text: item.requestedQty, alignment: 'right' },
        { text: item.pendingQty, alignment: 'right' },
        { text: item.issuedQty, alignment: 'right', bold: true },
        { text: item.issueRate, alignment: 'right' },
        { text: item.lineAmount, alignment: 'right', bold: true }
      ]))
    ];

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [20, 10, 20, 44],
      footer: (currentPage: number, pageCount: number) => ({
        margin: [20, 0, 20, 6],
        stack: [
          {
            canvas: [
              { type: 'line', x1: 0, y1: 0, x2: 555, y2: 0, lineWidth: 1, color: '#8da0b8' },
              { type: 'rect', x: 0, y: 5, w: 132, h: 2.6, color: '#153a6b' },
              { type: 'rect', x: 441, y: 5, w: 114, h: 2.6, color: '#d97706' }
            ]
          },
          {
            margin: [0, 6, 0, 0],
            columns: [
              {
                width: '*',
                text: 'Generated using VCS ERP',
                style: 'footerText'
              },
              {
                width: 90,
                text: `Page ${currentPage} of ${pageCount}`,
                style: 'footerPageText',
                alignment: 'right'
              }
            ]
          }
        ]
      }),
      background: this.previewData.logo
        ? {
            image: this.previewData.logo,
            alignment: 'center',
            width: 280,
            height: 280,
            opacity: 0.05,
            margin: [0, 180, 0, 0]
          }
        : undefined,
      content: [
        {
          canvas: [
            { type: 'rect', x: 0, y: 0, w: 551, h: 6, color: '#153a6b' },
            { type: 'rect', x: 405, y: 0, w: 146, h: 6, color: '#d97706' },
            { type: 'line', x1: 0, y1: 15, x2: 551, y2: 15, lineWidth: 0.7, color: '#e2e8f0' }
          ],
          margin: [0, -10, 0, 4]
        },
        {
          table: {
            widths: [146, '*', 146],
            body: [[
              this.previewData.logo
                ? {
                    stack: [
                      {
                        image: this.previewData.logo,
                        fit: [74, 74],
                        alignment: 'center',
                        margin: [0, 4, 0, 0]
                      }
                    ],
                    border: [false, false, false, false]
                  }
                : {
                    text: this.previewData.companyInitials || 'MI',
                    style: 'logoFallback',
                    border: [false, false, false, false]
                  },
              {
                stack: [
                  { text: this.previewData.companyName, style: 'companyHeader' },
                  this.previewData.companyAddress ? { text: this.previewData.companyAddress, style: 'companyMeta' } : '',
                  ...this.previewData.companyMeta.map((meta: string) => ({ text: meta, style: 'companyMeta' }))
                ].filter(Boolean),
                margin: [0, 2, 0, 0],
                border: [false, false, false, false]
              },
              {
                stack: [
                  { text: 'MATERIAL ISSUE', style: 'badge' },
                  { text: `Issue Code: ${this.previewData.issueCode}`, style: 'meta' },
                  { text: `Issue Date: ${this.previewData.issueDate}`, style: 'meta' },
                  { text: `Slip No: ${this.previewData.issueSlipNo || '-'}`, style: 'meta' },
                  { text: `Slip Date: ${this.previewData.issueSlipDate || '-'}`, style: 'meta' }
                ],
                fillColor: '#eef6ff',
                margin: [0, 6, 0, 0],
                border: [true, true, true, true]
              }
            ]]
          },
          layout: {
            hLineColor: () => '#d7deea',
            vLineColor: () => '#d7deea'
          }
        },
        {
          columns: [
            { width: '*', text: `Issue Type: ${this.previewData.issueType}`, style: 'summaryBox' },
            { width: '*', text: `Warehouse: ${this.previewData.warehouseName}`, style: 'summaryBox' },
            { width: '*', text: `Linked Requests: ${this.previewData.linkedRequests.length}`, style: 'summaryBox' },
            { width: '*', text: `Issue Amount: ${this.previewData.issueAmount}`, style: 'summaryBox' }
          ],
          columnGap: 8,
          margin: [0, 12, 0, 10]
        },
        this.previewData.linkedRequests.length > 0 ? {
          stack: [
            { text: 'Linked Material Requests', style: 'sectionHeader' },
            {
              columns: this.previewData.linkedRequests.map((request: any) => ({
                width: '*',
                stack: [
                  { text: request.code, bold: true },
                  { text: request.date, color: '#64748b' }
                ],
                fillColor: '#f8fafc',
                margin: [8, 8, 8, 8]
              })),
              columnGap: 8
            }
          ],
          margin: [0, 0, 0, 10]
        } : '',
        {
          table: {
            headerRows: 1,
            widths: [22, '*', 52, 56, 30, 40, 40, 40, 38, 36, 48],
            body
          },
          layout: {
            fillColor: (rowIndex: number) => rowIndex === 0 ? '#16323f' : rowIndex % 2 === 0 ? '#f8fafc' : '#ffffff',
            hLineColor: () => '#a7b5c8',
            vLineColor: () => '#a7b5c8'
          }
        },
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: 'Warehouse / Issue Point', style: 'sectionHeader' },
                { text: this.previewData.warehouseName, style: 'detailText' },
                this.previewData.warehouseAddress ? { text: this.previewData.warehouseAddress, style: 'detailText' } : '',
                this.previewData.remarks !== '-' ? { text: `Remarks: ${this.previewData.remarks}`, style: 'detailText', margin: [0, 10, 0, 0] } : '',
                { text: 'Amount In Words', style: 'sectionHeader', margin: [0, 10, 0, 2] },
                { text: this.previewData.issueAmountWords, style: 'detailText' }
              ].filter(Boolean)
            },
            {
              width: 200,
              stack: [
                { text: `For ${this.previewData.companyName}`, style: 'signatoryCompanyInline', alignment: 'right' },
                this.includeSignature && (this.previewData.stampImage || this.previewData.signatureImage)
                  ? {
                      columns: [
                        {
                          width: 72,
                          stack: [
                            this.previewData.stampImage
                              ? { image: this.previewData.stampImage, fit: [66, 66], opacity: 0.72, alignment: 'right', margin: [0, 4, -8, 0] }
                              : { text: '' }
                          ]
                        },
                        {
                          width: 118,
                          stack: [
                            this.previewData.signatureImage
                              ? { image: this.previewData.signatureImage, fit: [112, 38], alignment: 'right', margin: [0, 8, 0, 0] }
                              : { text: '' }
                          ]
                        }
                      ],
                      columnGap: -12,
                      margin: [0, 4, 0, 0]
                    }
                  : { text: '', margin: [0, 34, 0, 0] },
                this.previewData.signatoryName
                  ? { text: this.previewData.signatoryName, style: 'signatoryName', alignment: 'right', margin: [0, 4, 0, 0] }
                  : { text: '', margin: [0, 10, 0, 0] },
                { text: this.previewData.signatoryTitle || 'Authorized Signatory', style: 'signatoryRole', alignment: 'right' }
              ]
            }
          ],
          columnGap: 16,
          margin: [0, 12, 0, 0]
        }
      ],
      styles: {
        logoFallback: { fontSize: 22, bold: true, color: '#11284c', alignment: 'center', margin: [0, 22, 0, 0] },
        companyHeader: { fontSize: 17.8, bold: true, color: '#11284c', alignment: 'center' },
        companyMeta: { fontSize: 8.8, color: '#334155', alignment: 'center', margin: [0, 1, 0, 0] },
        badge: { fontSize: 11.8, bold: true, color: '#11284c', margin: [10, 10, 10, 7], alignment: 'left' },
        meta: { fontSize: 8.6, color: '#334155', margin: [10, 0, 10, 3], alignment: 'left' },
        summaryBox: { fontSize: 10, bold: true, color: '#0f172a', fillColor: '#f8fafc', margin: [8, 10, 8, 10] },
        sectionHeader: { fontSize: 10, bold: true, color: '#0f766e', margin: [0, 0, 0, 4] },
        detailText: { fontSize: 9, color: '#334155' },
        tableHeader: { fontSize: 8, bold: true, color: '#ffffff', alignment: 'center' },
        signatoryCompanyInline: { fontSize: 8.6, bold: true, color: '#334155' },
        signatoryName: { fontSize: 9.2, bold: true, color: '#11284c' },
        signatoryRole: { fontSize: 8.7, color: '#475569', bold: true },
        footerText: { fontSize: 6.8, color: '#64748b', margin: [0, 0, 0, 0] },
        footerPageText: { fontSize: 6.8, color: '#64748b' }
      },
      defaultStyle: { fontSize: 9, color: '#0f172a' }
    };

    this.pdfDoc = pdfMake.createPdf(docDefinition);
  }

  private extractItems(source: any): any[] {
    const candidates = [
      source?.materialissuedetails,
      source?.materialIssueDetails,
      source?.items,
      source?.details
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private extractRequestCards(source: any): any[] {
    const candidates = [
      source?.materialissuerequestcards,
      source?.materialIssueRequestCards,
      source?.linkedrequests,
      source?.requestcards
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate.map((card: any) => ({
          code: this.safeValue(card.code || card.materialrequestcode),
          date: this.formatDateValue(card.requestdate || card.date),
          remarks: this.safeValue(card.requestremarks || card.remarks, '')
        }));
      }
    }

    return [];
  }

  private pickEntity(source: any, keys: string[]): any {
    for (const key of keys) {
      const value = source?.[key];
      if (value && typeof value === 'object') {
        return Array.isArray(value) ? value[0] || {} : value;
      }
    }
    return {};
  }

  private buildCompanyMeta(company: any): string[] {
    return [
      this.metaLine('GSTIN', company.gstin),
      this.metaLine('PAN', company.pan),
      this.metaLine('Email', company.email),
      this.metaLine('Website', company.website),
      this.metaLine('Phone', company.mobile || company.phone)
    ].filter(Boolean) as string[];
  }

  private resolveImage(candidates: any[]): string | null {
    for (const candidate of candidates) {
      const normalized = this.normalizeImageCandidate(candidate);
      if (normalized) {
        return normalized;
      }
    }

    return null;
  }

  private normalizeImageCandidate(value: any): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const image = value.trim();
    if (!image) {
      return null;
    }

    if (image.startsWith('data:image')) {
      return image;
    }

    if (/^[A-Za-z0-9+/=\r\n]+$/.test(image) && image.length > 120) {
      return `data:image/png;base64,${image.replace(/\s+/g, '')}`;
    }

    return null;
  }

  private metaLine(label: string, value: any): string {
    const normalized = this.normalizePart(value);
    return normalized ? `${label}: ${normalized}` : '';
  }

  private firstMeaningfulText(candidates: any[]): string {
    for (const candidate of candidates) {
      const normalized = this.normalizePart(candidate);
      if (normalized) {
        return normalized;
      }
    }
    return '';
  }

  private joinAddress(parts: any[]): string {
    return parts.map((part) => this.normalizePart(part)).filter(Boolean).join(', ');
  }

  private normalizePart(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    const text = String(value).trim();
    return text === '-' ? '' : text;
  }

  private safeValue(value: any, fallback = '-'): string {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    return String(value);
  }

  private formatNumber(value: any): string {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) {
      return this.safeValue(value);
    }

    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  }

  private formatCurrency(value: any, withSymbol = false): string {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) {
      return this.safeValue(value);
    }

    const formatted = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    return withSymbol ? `${String.fromCharCode(8377)} ${formatted}` : formatted;
  }

  private formatDateValue(value: any): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.safeValue(value);
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  private getInitials(value: any): string {
    const text = this.safeValue(value, 'MI');
    return text
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();
  }
}
